import React, { useEffect } from "react";
import dayjs from "dayjs";
import { Form, Input, Button, Select, DatePicker, Upload, Divider, Flex, theme, Typography, type FormInstance, type UploadFile, Switch, InputNumber, Card, Descriptions, Tag, Row, Col } from "antd";
import { CheckCircleFilled, InboxOutlined } from "@ant-design/icons";
import SectionHeader from "../../shared/components/SectionHeader";
import { useFetchTypeContracts } from "../hooks/useFetchTypeContracts";
import { useFetchTemplatesByType } from "../../templates/hooks/useFetchTemplatesByType";
import { useFetchQuotes } from "../../quotes/hooks/useFetchQuotes";
import type { QuoteData } from "../../quotes/types";
import EmptyData from "../../shared/components/EmptyData";
import { useHybridResponsive } from "../../shared";
import type { TemplatePDF } from "../../templates/types/template";
import { PAYMENT_METHOD_TYPES } from "../utils/paymentMethods";

const { Text } = Typography;
const { Dragger } = Upload;

const FieldLabel: React.FC<{ text: string }> = ({ text }) => {
  const { token } = theme.useToken();
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: token.colorTextSecondary,
      }}
    >
      {text}
    </Text>
  );
};

interface ContractFormProps {
  form: FormInstance<any>;
  onFinish: (values: any) => void;
  isCreating?: boolean;
  isUploading?: boolean;
  showDetails?: boolean;
  hideQuoteSelect?: boolean;
  fileList?: UploadFile[];
  setFileList?: (fileList: UploadFile[]) => void;
}

const ContractFrom: React.FC<ContractFormProps> = ({
  form,
  onFinish,
  isCreating,
  isUploading,
  showDetails = true,
  hideQuoteSelect = false,
  fileList,
  setFileList,
}) => {
  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();

  const { data: typeContracts, isLoading: isLoadingTypes } = useFetchTypeContracts();
  const { data: templates, isLoading: isLoadingTemplates } = useFetchTemplatesByType('contract');
  const { data: quotes, isLoading: isLoadingQuotes } = useFetchQuotes();

  const selectedQuoteId = Form.useWatch("id_quote", form);
  const selectedQuote = quotes?.find((q: QuoteData) => q.id_quote === selectedQuoteId);

  const selectedTemplateId = Form.useWatch("id_template", form);
  const selectedTemplate = templates?.find((t: TemplatePDF) => t.id_template_pdf === selectedTemplateId);

  useEffect(() => {
    // Solo se sobreescribe template_text cuando el usuario realmente cambió
    // la plantilla desde el select (isFieldTouched). Sin este chequeo, al
    // editar un contrato existente este efecto se dispara en cuanto el
    // catálogo de plantillas termina de cargar y pisa el texto ya
    // guardado/editado del contrato con el texto genérico de la plantilla.
    if (selectedTemplate && form.isFieldTouched("id_template")) {
      form.setFieldsValue({
        template_text: selectedTemplate.template_text,
      });
    }
  }, [selectedTemplate, form]);

  const iStyle: React.CSSProperties = {
    borderRadius: token.borderRadiusLG,
  };

  return (
    <section style={{ maxWidth: "800px", margin: "0 auto" }}>
      <Row gutter={[24, 24]}>
        <Col span={24} md={isMobile || !showDetails ? 24 : 15}>
          <Form
            id="upload-contract-form"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            style={{ border: `1px solid ${token.colorBorderSecondary}`, padding: 24, backgroundColor: token.colorBgContainer, borderRadius: token.borderRadiusLG }}
          >
            {/* Hidden fields */}
            <Form.Item name="template_text" hidden>
              <Input />
            </Form.Item>

            {/* ── Section: General Info ── */}
            <Flex vertical gap={16} style={{ marginBottom: 28 }}>
              <SectionHeader title="Información General" />

              {/* Contract name */}
              <Form.Item
                name="contract_name"
                label={<FieldLabel text="Nombre del Contrato" />}
                rules={[{ required: true, message: "Campo requerido" }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  size="large"
                  placeholder="Ej. Alquiler Oficina Central"
                  style={iStyle}
                />
              </Form.Item>

              {/* Quote — hidden when parent already provides the selected quote */}
              {!hideQuoteSelect && (
                <Form.Item
                  name="id_quote"
                  label={<FieldLabel text="Cotización" />}
                  rules={[{ required: true, message: "Campo requerido" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    size="large"
                    loading={isLoadingQuotes}
                    disabled={isLoadingQuotes}
                    placeholder="Selecciona una cotización"
                    options={quotes?.map((q: any) => ({
                      value: q.id_quote,
                      label: `COT-${q.id_quote} - ${q.description_quote}`,
                    }))}
                    style={iStyle}
                  />
                </Form.Item>
              )}

              {/* Signing date */}
              <Form.Item
                name="signing_date"
                label={<FieldLabel text="Fecha de Firma" />}
                rules={[{ required: true, message: "Campo requerido" }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  size="large"
                  style={{ ...iStyle, width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/aaaa"
                />
              </Form.Item>

              {/* City — usada para llenar el placeholder de ciudad/estado del documento */}
              <Form.Item
                name="city"
                label={<FieldLabel text="Ciudad" />}
                rules={[{ required: true, message: "Campo requerido" }]}
                initialValue="Colima"
                style={{ marginBottom: 0 }}
              >
                <Input
                  size="large"
                  placeholder="Ej. Colima"
                  style={iStyle}
                />
              </Form.Item>

              {/* Template */}
              <Form.Item
                name="id_template"
                label={<FieldLabel text="Tipo de documento" />}
                rules={[{ required: true, message: "Campo requerido" }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  size="large"
                  loading={isLoadingTemplates}
                  disabled={isLoadingTemplates}
                  placeholder="Selecciona una plantilla"
                  options={templates?.map((t) => ({
                    value: t.id_template_pdf,
                    label: t.template_name,
                  }))}
                  style={iStyle}
                />
              </Form.Item>

              {/* Contract type */}
              <Form.Item
                name="id_type_contract"
                label={<FieldLabel text="Tipo de contrato" />}
                rules={[{ required: true, message: "Campo requerido" }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  size="large"
                  loading={isLoadingTypes}
                  disabled={isLoadingTypes}
                  placeholder="Selecciona un tipo de contrato"
                  options={typeContracts?.map((typeContract) => ({
                    value: typeContract.id_type_contract,
                    label: typeContract.type_contract,
                  }))}
                  style={iStyle}
                />
              </Form.Item>

              {/* Payment methods — un contrato puede tener más de uno */}
              <Form.Item
                name="payment_methods"
                label={<FieldLabel text="Métodos de Pago" />}
                rules={[{ required: true, message: "Selecciona al menos un método de pago" }]}
                extra="Puedes seleccionar más de un método de pago."
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  size="large"
                  placeholder="Selecciona uno o más métodos"
                  options={PAYMENT_METHOD_TYPES.map((method) => ({
                    value: method,
                    label: method,
                  }))}
                  style={iStyle}
                />
              </Form.Item>
            </Flex>

            <Divider style={{ margin: "0 0 24px" }} />

            {/* ── Section: Dates ── */}
            <Flex vertical gap={16} style={{ marginBottom: 28 }}>
              <SectionHeader title="Vigencia" />

              <Flex gap={12} wrap>
                {/* Start date */}
                <Form.Item
                  name="start_date"
                  label={<FieldLabel text="Fecha de Inicio" />}
                  rules={[{ required: true, message: "Campo requerido" }]}
                  style={{ marginBottom: 0, flex: "1 1 140px" }}
                >
                  <DatePicker
                    size="large"
                    style={{ ...iStyle, width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/aaaa"
                  />
                </Form.Item>

                {/* End date */}
                <Form.Item
                  name="end_date"
                  label={<FieldLabel text="Fecha de Vencimiento" />}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || !getFieldValue("start_date")) return Promise.resolve();
                        if (value.isAfter(getFieldValue("start_date"))) return Promise.resolve();
                        return Promise.reject(new Error("Debe ser posterior al inicio"));
                      },
                    }),
                  ]}
                  style={{ marginBottom: 0, flex: "1 1 140px" }}
                >
                  <DatePicker
                    size="large"
                    style={{ ...iStyle, width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/aaaa"
                  />
                </Form.Item>
              </Flex>
            </Flex>

            <Divider style={{ margin: "0 0 24px" }} />

            {/* ── Section: Payment & Partials ── */}
            <Flex vertical gap={16} style={{ marginBottom: 28 }}>
              <SectionHeader title="Condiciones de Pago" />

              <Flex gap={12} align="center">
                <Form.Item
                  name="partials"
                  label={<FieldLabel text="¿Maneja Parcialidades?" />}
                  valuePropName="checked"
                  initialValue="No"
                  getValueProps={(value) => ({ checked: value === 'Si' })}
                  getValueFromEvent={(checked) => (checked ? 'Si' : 'No')}
                  style={{ marginBottom: 0 }}
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Flex>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.partials !== currentValues.partials}
              >
                {({ getFieldValue }) =>
                  getFieldValue("partials") === "Si" ? (
                    <Flex gap={12} wrap>
                      <Form.Item
                        name="partials_total"
                        label={<FieldLabel text="Total de Parcialidades" />}
                        style={{ marginBottom: 0, flex: "1 1 140px" }}
                      >
                        <InputNumber
                          size="large"
                          min={1}
                          placeholder="Ej. 12"
                          style={{ ...iStyle, width: "100%" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="partials_percentage"
                        label={<FieldLabel text="Porcentaje" />}
                        style={{ marginBottom: 0, flex: "1 1 140px" }}
                      >
                        <InputNumber
                          size="large"
                          min={0}
                          max={100}
                          formatter={(value) => `${value}%`}
                          parser={(value) => value?.replace("%", "") as any}
                          placeholder="Ej. 50%"
                          style={{ ...iStyle, width: "100%" }}
                        />
                      </Form.Item>
                    </Flex>
                  ) : null
                }
              </Form.Item>
            </Flex>

            <Divider style={{ margin: "0 0 24px" }} />

            {/* ── Section: File upload ── */}
            <Flex vertical gap={16} style={{ marginBottom: 32 }}>
              {/* <SectionHeader title="Archivo Adjunto" /> */}
              {/* 
              <Form.Item
                name="file"
                rules={[{ required: true, message: "Adjunta un archivo" }]}
                style={{ marginBottom: 0 }}
              >
<Dragger
            name="file"
            accept=".pdf"
            maxCount={1}
            fileList={fileList}
            beforeUpload={(file) => {
              setFileList([file as unknown as UploadFile]);
              form.setFieldValue("file", file);
              return false;
            }}
            onRemove={() => {
              setFileList([]);
              form.setFieldValue("file", null);
            }}
            style={{
              borderRadius: token.borderRadiusLG,
              background: token.colorFillQuaternary,
              border: `2px dashed ${token.colorBorderSecondary}`,
              padding: "8px 0",
            }}
          >
            <Flex vertical align="center" gap={8} style={{ padding: "16px 24px" }}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: token.colorPrimaryBg,
                  color: token.colorPrimary,
                  fontSize: 24,
                  marginBottom: 4,
                }}
              >
                <InboxOutlined />
              </Flex>
              <Text strong style={{ fontSize: 14 }}>
                Subir Documento PDF / XML
              </Text>
              <Text type="secondary" style={{ fontSize: 13, textAlign: "center", maxWidth: 220 }}>
                Arrastra el archivo aquí o haz clic para explorar
              </Text>
            </Flex>
          </Dragger> 
              </Form.Item>
              */}
            </Flex>

            {/* ── Submit ── */}

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isLoadingTypes || isLoadingTemplates || isCreating || isUploading}
              icon={<CheckCircleFilled />}
              block
              style={{
                height: 54,
                borderRadius: token.borderRadiusLG,
                fontWeight: 800,
                fontSize: 16,
                letterSpacing: "0.01em",
                boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
              }}
            >
              Guardar Contrato
            </Button>

          </Form>
        </Col>
        <Col span={24} md={8}>
          {showDetails && !isMobile &&
            <div style={{ position: "sticky", top: 24 }}>
              <Flex vertical gap={24}>
                {selectedQuote ?
                  <SelectedQuoteComponent selectedQuote={selectedQuote} token={token} />
                  : <EmptyData description="No se ha seleccionado ninguna cotización" />
                }

                {selectedTemplate && (
                  <SelectedTemplateComponent selectedTemplate={selectedTemplate} token={token} />
                )}
              </Flex>
            </div>
          }
        </Col>
      </Row>

    </section>
  );
};

export default ContractFrom;

function SelectedQuoteComponent({ selectedQuote, token }: { selectedQuote: QuoteData, token: any }) {
  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <Text strong>Cotización Seleccionada</Text>
          <Tag color="blue">COT-{selectedQuote.id_quote}</Tag>
        </Flex>
      }
      style={{
        width: 350,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        border: `1px solid ${token.colorBorderSecondary}`,
      }}
      styles={{ body: { padding: "20px" } }}
    >
      <Descriptions column={1} size="small" layout="vertical">
        <Descriptions.Item label={<FieldLabel text="Descripción" />}>
          <Text strong>{selectedQuote.description_quote}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Monto Total" />}>
          <Text style={{ fontSize: 18, fontWeight: 800, color: token.colorPrimary }}>
            ${new Intl.NumberFormat().format(selectedQuote.total_quote)}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Fecha" />}>
          {dayjs(selectedQuote.date_quote).format("DD/MM/YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Vencimiento" />}>
          {dayjs(selectedQuote.date_expiration).format("DD/MM/YYYY")}
        </Descriptions.Item>
      </Descriptions>

      {selectedQuote.concepts && selectedQuote.concepts.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <FieldLabel text="Conceptos" />
          <Flex vertical gap={4} style={{ marginTop: 8 }}>
            {selectedQuote.concepts.map((c: any) => (
              <Tag key={c.id_concept} style={{ margin: 0 }}>
                {c.concept_name}
              </Tag>
            ))}
          </Flex>
        </>
      )}
    </Card>
  )
}

function SelectedTemplateComponent({ selectedTemplate, token }: { selectedTemplate: any, token: any }) {
  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <Text strong>Plantilla Seleccionada</Text>
        </Flex>
      }
      style={{
        width: 350,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        border: `1px solid ${token.colorBorderSecondary}`,
      }}
      styles={{ body: { padding: "20px" } }}
    >
      <Descriptions column={1} size="small" layout="vertical">
        <Descriptions.Item label={<FieldLabel text="Nombre" />}>
          <Text strong>{selectedTemplate.template_name}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Tipo" />}>
          <Tag color="cyan">{selectedTemplate.type_template}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Vista Previa" />}>
          <div style={{
            maxHeight: 200,
            overflowY: 'auto',
            padding: 8,
            background: token.colorFillAlter,
            borderRadius: token.borderRadius,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            border: `1px solid ${token.colorBorderSecondary}`
          }}>
            {selectedTemplate.template_text}
          </div>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}